import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { snackAdded } from "src/slices/system";

interface LinkTooltipProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  href: string;
  onEdit: (newHref: string) => void;
  onDelete: () => void;
  onClose: () => void;
  linkText?: string;
  editor: any;
  onMouseLeave?: () => void;
}

const LinkTooltip = ({
  open,
  anchorEl,
  href,
  linkText = "",
  onEdit,
  onDelete,
  onClose,
  editor,
  onMouseLeave,
}: LinkTooltipProps) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(linkText);
  const [editedHref, setEditedHref] = useState(href);
  const [newHref, setNewHref] = useState("");
  const newUrlInputRef = useRef<HTMLInputElement>(null);
  const isHoveringRef = useRef(false);

  useEffect(() => {
    if (isEditing && newUrlInputRef.current) {
      newUrlInputRef.current.focus({
        preventScroll: true,
      });
    }
  }, [isEditing]);

  const handleCopy = () => {
    navigator.clipboard.writeText(href);
    dispatch(
      snackAdded({
        message: "Link copied to clipboard",
        type: "success",
      })
    );
  };

  const handleOpen = () => {
    let formattedHref = href;
    if (!href.startsWith("http://") && !href.startsWith("https://")) {
      formattedHref = `https://${href}`;
    }
    window.open(formattedHref, "_blank");
  };

  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setIsEditing(true);
    setEditedText(linkText);
    setEditedHref(href);
  };

  const handleSave = () => {
    onEdit(newHref);
    setIsEditing(false);
  };

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    isHoveringRef.current = false;

    setTimeout(() => {
      if (!isHoveringRef.current) {
        onClose();
      }
    }, 50);
  };

  if (!open || !anchorEl) return null;

  return ReactDOM.createPortal(
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      className="MuiPopper-root"
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 10],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
          },
        },
      ]}
      style={{
        zIndex: 9999,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Paper
        elevation={3}
        sx={{
          p: 1,
          backgroundColor: "background.paper",
          borderRadius: 1,
          width: 250,
          position: "relative",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isEditing ? (
          <Box
            sx={{ p: 2 }}
            onClick={(e) => e.stopPropagation()}
            onPaste={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const pastedText = e.clipboardData.getData("text");
              setNewHref(pastedText);
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Current URL"
              value={editedHref}
              disabled
              sx={{
                mb: 2,
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#64748B",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="New URL"
              value={newHref}
              onChange={(e) => setNewHref(e.target.value)}
              inputRef={newUrlInputRef}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
              onPaste={(e: React.ClipboardEvent) => {
                e.preventDefault();
                e.stopPropagation();
                const pastedText = e.clipboardData.getData("text");
                if (pastedText) {
                  setNewHref(pastedText);
                }
              }}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "v") {
                  e.stopPropagation();
                  return;
                }
              }}
            />
            <Button
              fullWidth
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                py: 1,
              }}
            >
              Save Changes
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 1, wordBreak: "break-all", p: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  fontFamily: "Inter",
                  color: "#64748B",
                }}
                variant="caption"
                color="textSecondary"
              >
                {href}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <IconButton size="small" onClick={handleCopy} title="Copy link">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleOpen} title="Open link">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleEditClick}
                title="Edit link"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={onDelete} title="Remove link">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}
      </Paper>
    </Popper>,
    document.body
  );
};

export default LinkTooltip;
